import { useContext, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { NotificationResponse } from '@/api/data-contracts';
import { queryKeys } from '@/react-query/constants';
import { UserTypeContext } from '@/shared/lib/UserTypeContext/UserTypeContext';

import useNotificationsService from '../../api/notifications.service';
import useNotificationsStore from '../../model/notifications';

const useNotificationsDrawerData = () => {
  const { getNotifications } = useNotificationsService();
  const userType = useContext(UserTypeContext);
  const showReadNotifications = useNotificationsStore((s) => s.showReadNotifications);

  const fetchAll = showReadNotifications;

  const targetModule = useMemo(() => {
    if (!userType) return undefined;
    const moduleMap: Record<string, string> = {
      location: 'LOCATION',
      advertiser: 'ADVERTISER',
      backoffice: 'BACKOFFICE',
      service: 'SERVICES',
    };
    return moduleMap[userType];
  }, [userType]);

  const { data: allNotifications = [], isLoading } = useQuery<NotificationResponse[]>({
    queryKey: [
      queryKeys.notifications,
      'drawer',
      targetModule ?? 'all-modules',
      fetchAll ? 'all' : 'unread',
    ] as const,
    queryFn: async ({ signal }) =>
      getNotifications(
        {
          pageNumber: 0,
          numberOfItems: 1000,
          unreadOnly: !fetchAll,
          ...(targetModule ? { targetModule } : {}),
        },
        signal,
      ),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const sortedNotifications = useMemo(() => {
    if (!fetchAll) return allNotifications;
    return [...allNotifications].sort(
      (a, b) => new Date(b.audit.timestamp).getTime() - new Date(a.audit.timestamp).getTime(),
    );
  }, [allNotifications, fetchAll]);

  return { notifications: sortedNotifications, isLoading };
};

export default useNotificationsDrawerData;
